import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { useEffect, useState } from "react";
import { Organization } from "../../../../core/models/entities/Organization";
import { useErrorMessage } from "../../../hooks/useErrorMessage";
import { useCustomerActionHandler } from "../../customer-create/hooks/useCustomerActionHandler";
import { toast } from "react-toastify";
import {
  CustomerUpdateForm_VM,
  OrganizationUpdateForm,
} from "../component/customer-update-form.component";
import { LogoOptions } from "../../../../core/models/entities/LogoOptions";
import {
  Commission,
  CommissionProfile,
} from "../../../../core/models/entities/Commission";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { CreateUserDto } from "../../../../core/models/dtos/CreateUserDto";
import { withToasts } from "../../../components/Toast/Toast";

import { useNavigate } from "react-router-dom";

export function useCustomerFormUpdateViewModel(
  manufacturers?: Manufacturer[],
  customer?: Organization,
  logoOptions?: LogoOptions
): CustomerUpdateForm_VM {
  const navigate = useNavigate();
  const currentManu =
    manufacturers?.filter((m) =>
      customer?.excludedManufacturers.find((emId) => m.id === emId)
    ) ?? [];
  const { logoRepository, organizationRepository, userRepository } =
    useRepositoriesContext();
  const { profile } = useProfileContext();
  const { data: vendor } = organizationRepository.useVendor(
    profile?.userOrganizationInformations.organizationId
  );
  const [form, setForm] = useState<OrganizationUpdateForm>({
    id: customer?.id ?? 0,
    postCode: customer?.postCode ?? "",
    address: customer?.address ?? "",
    city: customer?.city ?? "",
    country: customer?.country ?? "",
    cvr: customer?.cvr ?? "",
    email: customer?.email ?? "",
    contactEmail: customer?.contactEmail ?? "",
    phone: customer?.phone ?? "",
    contactPhone: customer?.contactPhone ?? "",
    contactPerson: customer?.contactPerson ?? "",
    name: customer?.name ?? "",
    excludedManufacturers: currentManu,
    eori: customer?.eori ?? "",
    shippingName: customer?.shippingName ?? "",
    shippingAddress: customer?.shippingAddress ?? "",
    shippingPostCode: customer?.shippingPostCode ?? "",
    shippingCity: customer?.shippingCity ?? "",
    shippingCountry: customer?.shippingCountry ?? "",
    shippingContactPerson: customer?.shippingContactPerson ?? "",
    shippingContactPhone: customer?.shippingContactPhone ?? "",
    shippingContactEmail: customer?.shippingContactEmail ?? "",
    vatRate: customer?.vatRate ?? vendor?.defaultVatRate,
    hidePrices: customer?.hidePrices ?? false,
    // Round-trip only, no UI: the backend overwrites omitted fields with
    //  defaults (false/null), so leaving these out would reactivate a
    //  disabled customer and wipe its language on every save.
    disabled: customer?.disabled ?? false,
    language: customer?.language ?? null,
  });

  const { handleGoToCustomerList, handleUpdate, isLoading } =
    useCustomerActionHandler();
  const { tError } = useErrorMessage();

  const {
    data: users,
    isError: isUsersError,
    isLoading: isUsersLoading,
    isSuccess: isUsersSuccess,
  } = userRepository.useUsersOfOrganization(customer?.id ?? 0, {
    enabled: !!customer?.id,
  });

  const [commissionLoading, setCommissionLoading] = useState(false);
  const [userActionLoading, setUserActionLoading] = useState(false);

  const { data } = logoRepository.useTransferOptions(customer?.parentId);

  const [commission, setCommission] = useState<CommissionProfile | undefined>(
    customer?.commission
  );

  useEffect(() => {
    // Reinitialize the form whenever the customer changes.
    //  This should only happen on navigation, when the UI also changes.
    setForm({
      id: customer?.id ?? 0,
      postCode: customer?.postCode ?? "",
      address: customer?.address ?? "",
      city: customer?.city ?? "",
      country: customer?.country ?? "",
      cvr: customer?.cvr ?? "",
      email: customer?.email ?? "",
      contactEmail: customer?.contactEmail ?? "",
      phone: customer?.phone ?? "",
      contactPhone: customer?.contactPhone ?? "",
      contactPerson: customer?.contactPerson ?? "",
      name: customer?.name ?? "",
      excludedManufacturers: currentManu,
      eori: customer?.eori ?? "",
      shippingName: customer?.shippingName ?? "",
      shippingAddress: customer?.shippingAddress ?? "",
      shippingPostCode: customer?.shippingPostCode ?? "",
      shippingCity: customer?.shippingCity ?? "",
      shippingCountry: customer?.shippingCountry ?? "",
      shippingContactPerson: customer?.shippingContactPerson ?? "",
      shippingContactPhone: customer?.shippingContactPhone ?? "",
      shippingContactEmail: customer?.shippingContactEmail ?? "",
      vatRate: customer?.vatRate ?? vendor?.defaultVatRate,
      hidePrices: customer?.hidePrices ?? false,
      // Round-trip only, no UI — see initial form state above.
      disabled: customer?.disabled ?? false,
      language: customer?.language ?? null,
    });
    // Also update the commission when customer data changes
    setCommission(customer?.commission);
  }, [customer, vendor]);

  function handleCreateClick() {
    const formValid = !isCustomerUpdateFormInvalid(form);
    if (!formValid) {
      toast.error("Form is invalid");
      return;
    }

    async function transaction() {
      await handleUpdate({
        ...form,
        isVendor: false,
        excludedManufacturers: form.excludedManufacturers?.map((x) => x.id),
        commission: undefined,
      } as Organization);
      if (commission && customer) {
        setCommissionLoading(true);
        const result = await organizationRepository.setCommissionProfile(
          customer.id,
          commission
        );
        setCommissionLoading(false);
        return result;
      }
      return Promise.resolve();
    }

    transaction()
      .then(() => toast.success("Customer was updated"))
      .catch((e) => tError(e));
  }

  function handleCreateUser(user: CreateUserDto) {
    async function transaction() {
      user.organizationId = customer?.id ?? 0;
      return await userRepository.createUser(user);
    }

    return withToasts(
      transaction,
      "Creating user",
      "User created",
      "",
      setUserActionLoading
    );
  }

  function handleDeleteUser(userId: number) {
    async function transaction() {
      return await userRepository.deleteUser(userId);
    }

    return withToasts(
      transaction,
      "Deleting user",
      "User deleted",
      "Failed to delete user",
      setUserActionLoading
    );
  }

  function updateCommission(commission: CommissionProfile): void {
    setCommission(commission);
  }

  function handleCancel() {
    if (customer?.isVendor) {
      navigate("/vendor");
    } else {
      handleGoToCustomerList();
    }
  }

  return {
    onCreate: handleCreateClick,
    onCancel: handleCancel,
    form: form,
    setForm: setForm,
    manufactures: manufacturers ?? [],
    isFormValid: !isCustomerUpdateFormInvalid(form),
    isLoading: isLoading || commissionLoading || userActionLoading,
    updateCommission: updateCommission,
    vendorId: customer?.parentId ?? 0,
    logoOptions: logoOptions ?? ({} as LogoOptions),
    commission:
      commission ??
      ({
        typeOnly: [] as Commission[],
        methods: [] as Commission[],
      } as CommissionProfile),
    commissionOptions: {
      types: logoOptions?.transferLogoTypes ?? [],
      transfers: data?.transferMethods ?? [],
    },
    users: users || [],
    onCreateUser: handleCreateUser,
    onDeleteUser: handleDeleteUser,
  };
}

export function isCustomerUpdateFormInvalid(
  form: OrganizationUpdateForm
): boolean {
  return (
    !form.name ||
    !form.cvr ||
    !form.phone ||
    !form.address ||
    !form.email ||
    !form.contactEmail ||
    !form.contactPerson ||
    !form.contactPhone
  );
}
